import { useEffect, useMemo, useState } from "react";
import { getPerformanceSummary } from "./StockTradeService";
import { evaluateStockAlerts, getStockAlertEvents, getStockAlertTriggers, saveStockAlertTriggers } from "./StockAlertService";
import { fetchNsePrices, importStockPrices } from "./StockPriceService";

const emptyTrigger = { triggerType: "SECTOR_EXPOSURE", triggerValue: "", isActive: true };
const TRIGGER_HINTS = {
  SECTOR: "Sector:25",
  COUNTRY: "Country:40",
  SECTOR_EXPOSURE: "Sector:25",
  COUNTRY_EXPOSURE: "Country:40",
  POSITION_PNL: "SYMBOL:-10",
  PRICE_CHANGE: "SYMBOL:1M:-8",
};

export default function StockAlerts() {
  const [performance, setPerformance] = useState({ rows: [] });
  const [triggers, setTriggers] = useState([emptyTrigger]);
  const [events, setEvents] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [nseIndex, setNseIndex] = useState("NIFTY TOTAL MARKET");
  const [isFetching, setIsFetching] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
    return `${Number(value).toFixed(2)}%`;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
    return Number(value).toFixed(2);
  };

  const changeClass = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "";
    return Number(value) >= 0 ? "stocks-good" : "stocks-bad";
  };

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [perf, trg, evt] = await Promise.all([
      getPerformanceSummary(),
      getStockAlertTriggers(),
      getStockAlertEvents(),
    ]);
    setPerformance(perf || { rows: [] });
    setTriggers(trg && trg.length ? trg : [emptyTrigger]);
    setEvents(evt || []);
  };

  const updateTrigger = (idx, key, value) => {
    setTriggers((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t))
    );
  };

  const addTrigger = () => {
    setTriggers((prev) => [...prev, { ...emptyTrigger }]);
  };

  const removeTrigger = (idx) => {
    setTriggers((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveTriggers = async () => {
    setIsSaving(true);
    try {
      const payload = triggers
        .filter((t) => t.triggerValue && t.triggerValue.trim())
        .map((t) => ({
          triggerType: t.triggerType,
          triggerValue: t.triggerValue.trim(),
          isActive: t.isActive !== false,
        }));
      await saveStockAlertTriggers(payload);
      await load();
      window.alert("Triggers saved.");
    } catch (err) {
      window.alert(err?.message || "Failed to save triggers.");
    } finally {
      setIsSaving(false);
    }
  };

  const runAlerts = async () => {
    setIsEvaluating(true);
    try {
      await evaluateStockAlerts();
      await load();
      window.alert("Alerts evaluated.");
    } catch (err) {
      window.alert(err?.message || "Alert evaluation failed.");
    } finally {
      setIsEvaluating(false);
    }
  };
  const handlePriceImport = async (file) => {
    if (!file) return;
    setIsImporting(true);
    try {
      const result = await importStockPrices(file);
      const imported = result?.importedCount ?? 0;
      const updated = result?.updatedCount ?? 0;
      const skipped = result?.skippedCount ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors.slice(0, 5).join("\n") : "";
      await load();
      window.alert(
        `Price import complete. Imported: ${imported}, Updated: ${updated}, Skipped: ${skipped}.` +
          (errors ? `\nErrors:\n${errors}` : "")
      );
    } catch (err) {
      window.alert(err?.message || "Price import failed.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleNseFetch = async () => {
    setIsFetching(true);
    try {
      const result = await fetchNsePrices(nseIndex);
      const imported = result?.importedCount ?? 0;
      const updated = result?.updatedCount ?? 0;
      const skipped = result?.skippedCount ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors.slice(0, 5).join("\n") : "";
      await load();
      window.alert(
        `NSE fetch complete. Imported: ${imported}, Updated: ${updated}, Skipped: ${skipped}.` +
          (errors ? `\nErrors:\n${errors}` : "")
      );
    } catch (err) {
      window.alert(err?.message || "NSE fetch failed.");
    } finally {
      setIsFetching(false);
    }
  };

  const columns = [
    { key: "symbol", label: "Symbol" },
    { key: "latestPrice", label: "Last Price" },
    { key: "change15d", label: "15D" },
    { key: "change1m", label: "1M" },
    { key: "change2m", label: "2M" },
    { key: "changeQuarter", label: "Q" },
    { key: "changeHalfYear", label: "6M" },
    { key: "changeYear", label: "1Y" },
  ];

  const perfRows = useMemo(() => performance?.rows || [], [performance]);

  return (
    <div className="stocks-section">
      <div className="card">
        <div className="card-title">Price Upload</div>
        <div className="row-actions">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handlePriceImport(e.target.files[0])}
          />
          <span className="stocks-muted">
            CSV columns: `symbol`, `price_date`, `price`
          </span>
        </div>
        <div className="row-actions" style={{ marginTop: 10 }}>
          <select value={nseIndex} onChange={(e) => setNseIndex(e.target.value)}>
            <option value="NIFTY TOTAL MARKET">NIFTY TOTAL MARKET</option>
            <option value="NIFTY 500">NIFTY 500</option>
            <option value="NIFTY 50">NIFTY 50</option>
          </select>
          <button className="btn btn-primary" onClick={handleNseFetch} disabled={isFetching}>
            {isFetching ? "Fetching..." : "Fetch NSE Prices"}
          </button>
          <span className="stocks-muted">Uses NSE index snapshot data.</span>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Performance Snapshots</div>
        <div className="data-table-wrap">
          <table className="stocks-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
                {perfRows.map((row) => (
                <tr key={row.stockId}>
                  <td>{row.symbol}</td>
                  <td>{formatPrice(row.latestPrice)}</td>
                  <td className={changeClass(row.change15d)}>{formatPercent(row.change15d)}</td>
                  <td className={changeClass(row.change1m)}>{formatPercent(row.change1m)}</td>
                  <td className={changeClass(row.change2m)}>{formatPercent(row.change2m)}</td>
                  <td className={changeClass(row.changeQuarter)}>{formatPercent(row.changeQuarter)}</td>
                  <td className={changeClass(row.changeHalfYear)}>{formatPercent(row.changeHalfYear)}</td>
                  <td className={changeClass(row.changeYear)}>{formatPercent(row.changeYear)}</td>
                </tr>
              ))}
              {perfRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>No performance data yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Alert Rules</div>
        <div className="stocks-trigger-help">
          Alerts fire when portfolio conditions are met. Examples:
          <span className="stocks-trigger-eg">Sector exposure &gt; 25% → "IT:25"</span>
          <span className="stocks-trigger-eg">Stock PnL drops below -10% → "RELIANCE:-10"</span>
          <span className="stocks-trigger-eg">Price fell 8% over 1 month → "RELIANCE:1M:-8"</span>
        </div>
        {triggers.map((t, idx) => (
          <div key={`trigger-${idx}`} className="stocks-trigger-row">
            <select
              value={t.triggerType}
              onChange={(e) => updateTrigger(idx, "triggerType", e.target.value)}
            >
              <option value="SECTOR_EXPOSURE">Sector over-weight (e.g. IT:25)</option>
              <option value="COUNTRY_EXPOSURE">Country exposure % (e.g. India:40)</option>
              <option value="POSITION_PNL">Stock P&L% alert (e.g. RELIANCE:-10)</option>
              <option value="PRICE_CHANGE">Price change % (e.g. TCS:1M:-8)</option>
              <option value="SECTOR">Sector match (legacy)</option>
              <option value="COUNTRY">Country match (legacy)</option>
            </select>
            <input
              value={t.triggerValue}
              onChange={(e) => updateTrigger(idx, "triggerValue", e.target.value)}
              placeholder={TRIGGER_HINTS[t.triggerType] || "Value"}
              style={{ minWidth: 160 }}
            />
            <label className="inline-checkbox">
              <input
                type="checkbox"
                checked={t.isActive !== false}
                onChange={(e) => updateTrigger(idx, "isActive", e.target.checked)}
              />
              Active
            </label>
            <button className="btn btn-danger" onClick={() => removeTrigger(idx)}>
              Remove
            </button>
          </div>
        ))}
        <div className="row-actions" style={{ marginTop: 10 }}>
          <button className="btn btn-secondary" onClick={addTrigger}>
            Add Trigger
          </button>
          <button className="btn btn-primary" onClick={saveTriggers} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Triggers"}
          </button>
          <button className="btn btn-secondary" onClick={runAlerts} disabled={isEvaluating}>
            {isEvaluating ? "Evaluating..." : "Run Alerts"}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Recent Alerts</div>
        <div className="stocks-alert-list">
          {events.map((e) => {
            const fmtDate = (str) => {
              if (!str) return "";
              try { return new Date(str).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
              catch { return str; }
            };
            return (
              <div key={e.id} className="stocks-alert-item">
                <div className="stocks-alert-header">
                  <div className="stocks-alert-title">{e.title}</div>
                  {e.createdAt && <div className="stocks-alert-time">{fmtDate(e.createdAt)}</div>}
                </div>
                <div className="stocks-alert-meta">{e.message}</div>
              </div>
            );
          })}
          {events.length === 0 && (
            <div className="stocks-empty">
              No alerts fired yet. Set up trigger rules above, then click "Run Alerts" or wait for the daily evaluation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
